import List, { ListItem } from '../list/list.component'
import { CurecallBadge, CurecallTag, CurecallTooltip, ECurecallBadgeColor } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { TNavigationLocales } from 'src/types/locales'
import { getFormattedDateWithHours } from 'src/utils/date-formatter.util'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'
import { EVariableType, TPatientVariable } from 'src/types/variable'
import { EPatientFormStatus, TPatientForm } from 'src/types/form'
import PatientVariableDocument from './patient-variable-document.component'

interface PatientFormCardProps {
  patientForm: TPatientForm
}

const PatientFormCard = ({ patientForm }: PatientFormCardProps) => {
  const { i18n, t } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const { displayTimezone } = useTimezonePreference()
  const patientVariables = patientForm?.PatientVariables || []

  const getBadgeData = (patientVariable: TPatientVariable) => {
    const { value, name } = patientVariable
    const getMaxValue = (variableName?: string) => {
      switch (variableName) {
        case 'bleedingIntensity':
          return '3'
        default:
          return null
      }
    }
    switch (typeof value) {
      case 'boolean':
        return value ? t('common.yes') : t('common.no')
      case 'number': {
        const maxValue = patientVariable?.Variable?.maxValue || getMaxValue(name)
        if (maxValue) {
          return `${value} / ${maxValue}`
        }
        return `${value}`
      }
      default:
        return value?.toString()
    }
  }

  const getVariableValue = (patientVariable: TPatientVariable) => {
    if (patientVariable.Variable?.type === EVariableType.MEDIA) {
      return (
        <PatientVariableDocument variableId={patientVariable.VariableId} patientFormId={patientForm.id} />
      )
    }
    return (
      <CurecallBadge color={ECurecallBadgeColor.SMOOTH_PRIMARY}>
        {getBadgeData(patientVariable)}
      </CurecallBadge>
    )
  }

  const list: ListItem[] = patientVariables?.map((patientVariable) => {
    return {
      label:
        getTranslatedLabel(locale, patientVariable?.Variable?.intlLabel) ||
        patientVariable?.Variable?.label ||
        patientVariable?.label ||
        '',
      value: getVariableValue(patientVariable),
    }
  })

  const formStatusToText: Record<
    EPatientFormStatus,
    { badgeLabel: string; tooltipContent: string; badgeStatus: string }
  > = {
    [EPatientFormStatus.UNSTARTED]: {
      badgeLabel: t('components.patient.patient-file.body.followup-details.form-not-started'),
      tooltipContent: t('components.patient.patient-file.body.followup-details.waiting-patient-answer'),
      badgeStatus: 'INFORMATION',
    },
    [EPatientFormStatus.IN_PROGRESS]: {
      badgeLabel: t('components.patient.patient-file.body.followup-details.form-in-progress'),
      tooltipContent: t('components.patient.patient-file.body.followup-details.patient-in-answering'),
      badgeStatus: 'WARNING',
    },
    [EPatientFormStatus.COMPLETED]: {
      badgeLabel: t('components.patient.patient-file.body.followup-details.form-completed'),
      tooltipContent: t('components.patient.patient-file.body.followup-details.patient-has-answered'),
      badgeStatus: 'SUCCESS',
    },
    [EPatientFormStatus.EXPIRED]: {
      badgeLabel: t('components.patient.patient-file.body.followup-details.form-expired'),
      tooltipContent: t(
        'components.patient.patient-file.body.followup-details.patient-cannot-answer-anymore',
      ),
      badgeStatus: 'INFORMATION',
    },
    [EPatientFormStatus.CLOSED]: {
      badgeLabel: t('components.patient.patient-file.body.followup-details.form-expired'),
      tooltipContent: t(
        'components.patient.patient-file.body.followup-details.patient-cannot-answer-anymore',
      ),
      badgeStatus: 'INFORMATION',
    },
  }

  return (
    <div className="c-rounded-xl c-border-grey-300 c-border-2 c-p-6 c-min-w-full c-w-full">
      <div className="c-flex c-gap-4">
        <h3 className="c-font-bold c-text-base">
          {getTranslatedLabel(locale, patientForm?.Form?.intlLabel)}
        </h3>
        <CurecallTooltip
          content={formStatusToText[patientForm.status].tooltipContent}
          tooltipPosition="right"
          contentWrapperClassName="tw-w-max"
        >
          <CurecallTag status={formStatusToText[patientForm.status].badgeStatus}>
            {formStatusToText[patientForm.status].badgeLabel}
          </CurecallTag>
        </CurecallTooltip>
      </div>

      <p className="c-text-muted c-mb-5 c-mt-2 c-flex c-gap-2">
        {t('components.patient.patient-file.body.followup-details.control-send-date', {
          date: getFormattedDateWithHours(t, patientForm.createdAt, false, displayTimezone),
        })}
      </p>

      {patientVariables?.length > 0 && <List list={list} />}
    </div>
  )
}

export default PatientFormCard
